(() => {
  const root = document.getElementById('mse-q4-upsell');
  if (!root) return;

  const threshold = Number(root.dataset.threshold || 8995);
  const discountCents = Number(root.dataset.discountCents || 1000);
  const addErrorMessage = root.dataset.addError || '';
  const status = root.querySelector('[data-mse-upsell-status]');
  const productRail = root.querySelector('[data-mse-upsell-products]');
  const skipButton = root.querySelector('[data-mse-upsell-skip]');
  const closeButton = root.querySelector('[data-mse-upsell-close]');
  let lastFocus = null;
  let busy = false;
  let hydrationPromise = null;

  const coreHandles = new Set([
    'myromy-brot-frische-topf-brottopf-3-5l-signature',
    'myromy-brot-frische-topf-brottopf-6l-signature',
    'myromy-multi-braeter-brotbackform-3-5l-signature',
    'myromy-multi-braeter-brotbackform-6l-signature',
    'myromy-brot-frische-topf-brottopf-3-5l-coffee',
    'myromy-brot-frische-topf-brottopf-6l-coffee',
    'myromy-multi-braeter-brotbackform-3-5l-coffee',
    'myromy-multi-braeter-brotbackform-6l-coffee'
  ]);

  const routeRoot = window.Shopify?.routes?.root || '/';
  const cartUrl = routeRoot + 'cart.js';
  const addUrl = routeRoot + 'cart/add.js';
  const checkoutUrl = routeRoot + 'checkout';

  const goCheckout = () => {
    window.location.assign(checkoutUrl);
  };

  const fetchJson = async (url, timeoutMs = 6000) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Request unavailable');
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const fetchCart = () => fetchJson(cartUrl, 6000);

  const hasMarkedUpsell = (cart) => (cart.items || []).some(
    (item) => item.properties && String(item.properties._myromy_q4_upsell) === '1'
  );

  const isSetLike = (item) => {
    const handle = String(item?.handle || '').toLowerCase();
    const title = String(item?.product_title || item?.title || '').toLowerCase();
    return handle.includes('-set') || handle.includes('set-') || title.includes('-set') || title.includes(' set');
  };

  const hasDiscount = (cart) => {
    if ((cart.cart_level_discount_applications || []).length > 0) return true;
    return (cart.items || []).some((item) =>
      (item.discounts || []).length > 0 ||
      (item.line_level_discount_allocations || []).length > 0
    );
  };

  const isSimpleCoreCart = (cart) => {
    const items = cart.items || [];
    if (items.length !== 1) return false;
    if (hasDiscount(cart) || items.some(isSetLike)) return false;

    const handle = String(items[0]?.handle || '').toLowerCase();
    if (!coreHandles.has(handle)) return false;

    return Number(cart.items_subtotal_price || 0) >= threshold;
  };

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'de-DE';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (_) {
      return (Number(cents || 0) / 100).toFixed(2).replace('.', ',') + ' €';
    }
  };

  const productHandles = () => [...new Set(
    [...root.querySelectorAll('[data-product-handle]')]
      .map((node) => String(node.dataset.productHandle || '').trim())
      .filter(Boolean)
  )];

  const firstAvailableVariant = (product) =>
    (product?.variants || []).find((variant) => variant && variant.available) || null;

  const normalizeImage = (url) => {
    const value = String(url || '');
    return value.startsWith('//') ? 'https:' + value : value;
  };

  const applyProduct = (handle, product) => {
    const variant = firstAvailableVariant(product);
    const available = Boolean(product?.available && variant);
    const selector = '[data-product-handle="' + CSS.escape(handle) + '"]';

    root.querySelectorAll(selector).forEach((node) => {
      node.dataset.available = available ? 'true' : 'false';

      if (node.matches('[data-mse-upsell-edition]')) {
        node.disabled = !available;
        node.hidden = !available;
      }

      if (node.matches('[data-mse-upsell-add]')) {
        node.disabled = !available;
        if (available) node.dataset.variantId = String(variant.id || '');
        else delete node.dataset.variantId;
      }

      if (node.matches('.mse-q4-upsell-card__price')) {
        const original = node.querySelector('[data-mse-upsell-original-price]');
        const offer = node.querySelector('[data-mse-upsell-offer-price]');
        if (original) original.textContent = available ? money(variant.price) : '';
        if (offer) offer.textContent = available ? money(Math.max(0, Number(variant.price || 0) - discountCents)) : '';
      }

      const image = node.querySelector?.('[data-mse-upsell-image]');
      if (image) {
        const imageUrl = normalizeImage(product?.featured_image || product?.images?.[0] || '');
        if (available && imageUrl) {
          image.src = imageUrl;
          image.alt = String(product?.title || '');
          image.hidden = false;
        } else {
          image.hidden = true;
          image.removeAttribute('src');
        }
      }
    });
  };

  const hydrateOffers = async () => {
    if (hydrationPromise) return hydrationPromise;
    hydrationPromise = Promise.allSettled(
      productHandles().map(async (handle) => {
        try {
          const product = await fetchJson(routeRoot + 'products/' + encodeURIComponent(handle) + '.js', 6000);
          applyProduct(handle, product);
        } catch (_) {
          applyProduct(handle, null);
        }
      })
    ).then(() => {
      root.querySelectorAll('[data-mse-upsell-card]').forEach((card) => {
        const usable = [...card.querySelectorAll('[data-mse-upsell-edition]')]
          .some((button) => button.dataset.available === 'true' && !button.disabled);
        card.hidden = !usable;
      });
      return hasUsableOffer();
    });
    return hydrationPromise;
  };

  const getAddButtons = () => [...root.querySelectorAll('[data-mse-upsell-add]')];

  const hasUsableOffer = () => Boolean(
    status &&
    productRail &&
    skipButton &&
    closeButton &&
    getAddButtons().some((button) => !button.disabled && Number(button.dataset.variantId || 0) > 0)
  );

  const setCardEdition = (card, requestedEdition) => {
    if (!card) return false;
    const switches = [...card.querySelectorAll('[data-mse-upsell-edition]')]
      .filter((button) => button.dataset.available === 'true' && !button.disabled && !button.hidden);

    if (switches.length === 0) {
      card.hidden = true;
      return false;
    }

    const selectedSwitch = switches.find((button) => button.dataset.edition === requestedEdition) || switches[0];
    const selectedEdition = selectedSwitch.dataset.edition;
    if (!selectedEdition) {
      card.hidden = true;
      return false;
    }

    card.hidden = false;
    card.dataset.activeEdition = selectedEdition;
    card.querySelectorAll('[data-mse-upsell-edition]').forEach((button) => {
      const active = !button.hidden && button.dataset.edition === selectedEdition;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    card.querySelectorAll('[data-mse-upsell-state]').forEach((state) => {
      const available = state.dataset.available !== 'false';
      state.hidden = !available || state.dataset.edition !== selectedEdition;
    });
    return true;
  };

  const configureOffer = (cart) => {
    if (!productRail) return 0;

    const item = (cart.items || [])[0];
    const cartText = String(item?.handle || '') + ' ' + String(item?.product_title || '') + ' ' + String(item?.variant_title || '');
    const preferredEdition = cartText.toLowerCase().includes('coffee') ? 'coffee' : 'signature';
    const cards = [...productRail.querySelectorAll('[data-mse-upsell-card]')];

    let visibleCount = 0;
    cards.forEach((card) => {
      if (setCardEdition(card, preferredEdition)) visibleCount += 1;
    });

    productRail.dataset.visibleCount = String(visibleCount);
    productRail.scrollLeft = 0;
    root.dataset.preferredEdition = preferredEdition;
    root.classList.toggle('is-smart-grid', visibleCount > 0);
    return visibleCount;
  };

  const open = (cart) => {
    if (!hasUsableOffer()) return false;
    try {
      lastFocus = document.activeElement;
      if (status) status.textContent = '';

      if (configureOffer(cart) === 0) return false;

      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mse-q4-upsell-open');
      window.setTimeout(() => closeButton?.focus(), 0);
      return true;
    } catch (_) {
      return false;
    }
  };

  const close = () => {
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mse-q4-upsell-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  const handleCheckoutIntent = async () => {
    if (busy) return;

    busy = true;
    try {
      const cart = await fetchCart();

      if (hasMarkedUpsell(cart)) {
        goCheckout();
        return;
      }

      if (!isSimpleCoreCart(cart)) {
        goCheckout();
        return;
      }

      const ready = await hydrateOffers();
      if (ready && open(cart)) return;

      goCheckout();
    } catch (_) {
      goCheckout();
    } finally {
      busy = false;
    }
  };

  const isCartCheckoutForm = (form) => Boolean(
    form &&
    form.querySelector('[name="checkout"]') &&
    form.closest('#cart-drawer, .shopify-section--main-cart')
  );

  const notificationCheckoutTarget = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    for (const node of path) {
      if (!(node instanceof Element)) continue;

      const insideNotification = node.matches('cart-notification-drawer') || node.closest?.('cart-notification-drawer');
      if (!insideNotification) continue;

      if (
        node.matches('[name="checkout"], [data-checkout], a[href*="/checkout"], button[href*="/checkout"]')
      ) {
        return node;
      }
    }

    const target = event.target instanceof Element ? event.target : null;
    return target?.closest(
      'cart-notification-drawer [name="checkout"], cart-notification-drawer [data-checkout], cart-notification-drawer a[href*="/checkout"], cart-notification-drawer button[href*="/checkout"]'
    ) || null;
  };

  document.addEventListener('click', (event) => {
    const notificationTarget = notificationCheckoutTarget(event);
    if (notificationTarget) {
      event.preventDefault();
      event.stopPropagation();
      handleCheckoutIntent();
      return;
    }

    const target = event.target instanceof Element ? event.target.closest('[name="checkout"]') : null;
    if (!target) return;
    const form = target.form || target.closest('form');
    if (!isCartCheckoutForm(form)) return;
    event.preventDefault();
    event.stopPropagation();
    handleCheckoutIntent();
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!isCartCheckoutForm(form)) return;
    if (event.submitter && event.submitter.getAttribute('name') !== 'checkout') return;
    event.preventDefault();
    event.stopPropagation();
    handleCheckoutIntent();
  }, true);

  root.addEventListener('click', async (event) => {
    const editionButton = event.target.closest('[data-mse-upsell-edition]');
    if (editionButton) {
      const card = editionButton.closest('[data-mse-upsell-card]');
      setCardEdition(card, editionButton.dataset.edition || 'signature');
      return;
    }

    const clickedClose = event.target.closest('[data-mse-upsell-close]');
    if (clickedClose) {
      close();
      return;
    }

    const clickedSkip = event.target.closest('[data-mse-upsell-skip]');
    if (clickedSkip) {
      goCheckout();
      return;
    }

    const addButton = event.target.closest('[data-mse-upsell-add]');
    if (!addButton) return;
    if (busy) return;

    const variantId = Number(addButton.dataset.variantId || 0);
    if (!variantId) return;

    busy = true;
    root.classList.add('is-loading');
    getAddButtons().forEach((button) => { button.disabled = true; });
    if (status) status.textContent = '';

    try {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 6000);
      let response;
      try {
        response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: { _myromy_q4_upsell: '1' }
            }]
          }),
          signal: controller.signal
        });
      } finally {
        window.clearTimeout(timeout);
      }

      if (!response || !response.ok) throw new Error('Add failed');
      goCheckout();
    } catch (_) {
      if (status && addErrorMessage) status.textContent = addErrorMessage;
      busy = false;
      root.classList.remove('is-loading');
      getAddButtons().forEach((button) => {
        if (button.dataset.available === 'true') button.disabled = false;
      });
    }
  });

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
})();
